/**
 * 報表範本庫管理頁
 * Studio → 報表範本庫
 * 功能：左側分類 Tree、批量上傳、多選刪除、Placeholder 預覽、變量綁定
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Button, Space, Table, Tag, Drawer, Form, Input, Upload, message,
  Popconfirm, Typography, Tooltip, Divider, Badge, Spin, Modal, Row, Col,
  List, Result, Menu, AutoComplete,
} from 'antd';
import {
  UploadOutlined, DeleteOutlined, ReloadOutlined, DownloadOutlined,
  FileWordOutlined, FileExcelOutlined, FilePptOutlined, FileUnknownOutlined,
  EditOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined,
  LoadingOutlined, CloudUploadOutlined, AppstoreOutlined, FolderOutlined,
  ExclamationCircleOutlined, TagsOutlined, SearchOutlined, CloseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import JSZip from 'jszip';
import { reportTemplatesApi } from '../../../services/reportTemplatesApi';
import { useLanguage } from '../../../contexts/LanguageContext';

const { Title, Text } = Typography;
const { TextArea } = Input;

const FILE_TYPE_COLOR = { docx: 'blue', xlsx: 'green', pptx: 'orange' };
const FILE_TYPE_LABEL = { docx: 'Word', xlsx: 'Excel', pptx: 'PowerPoint' };
const FILE_TYPE_ICON = {
  docx: <FileWordOutlined style={{ color: '#1677ff' }} />,
  xlsx: <FileExcelOutlined style={{ color: '#52c41a' }} />,
  pptx: <FilePptOutlined style={{ color: '#fa8c16' }} />,
};
const PLACEHOLDER_TYPE_COLOR = { PV: 'blue', EFORM: 'purple', SYS: 'cyan', PHOTO: 'orange' };
const ACCEPTED_FORMATS = '.docx,.xlsx,.pptx,.zip';
const VALID_TEMPLATE_EXTS = new Set(['docx', 'xlsx', 'pptx']);
const ALL_KEY = '__all__';
const UNCATEGORIZED_KEY = '__uncategorized__';

const fileBaseName = (filename) => filename.replace(/\.(docx|xlsx|pptx)$/i, '').trim();

export default function ReportTemplatesPage() {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── 分類篩選 ──────────────────────────────────────────────────────
  const [selectedCategory, setSelectedCategory] = useState(ALL_KEY);

  // ── 搜尋 ──────────────────────────────────────────────────────────
  const [searchKeyword, setSearchKeyword] = useState('');

  // ── 多選 ──────────────────────────────────────────────────────────
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchDownloading, setBatchDownloading] = useState(false);
  const [pageSize, setPageSize] = useState(30);

  // ── 批量上傳 Drawer ──────────────────────────────────────────────
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const [queue, setQueue] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchDone, setBatchDone] = useState(false);
  // 批量上傳時共用的預設分類
  const [batchCategory, setBatchCategory] = useState('');

  // ── 詳情 Drawer ──────────────────────────────────────────────────
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);

  // ── 編輯名稱 Modal ───────────────────────────────────────────────
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm] = Form.useForm();

  // ── Load ─────────────────────────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await reportTemplatesApi.list());
    } catch (e) {
      message.error(t('reportTemplates.loadFailed') + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ── 分類計算 ──────────────────────────────────────────────────────
  const categories = useMemo(() => {
    const set = new Set(templates.map(t => t.category).filter(Boolean));
    return Array.from(set).sort();
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    let list = templates;
    if (selectedCategory !== ALL_KEY) {
      list = selectedCategory === UNCATEGORIZED_KEY
        ? list.filter(t => !t.category)
        : list.filter(t => t.category === selectedCategory);
    }
    const kw = searchKeyword.trim().toLowerCase();
    if (kw) {
      list = list.filter(t =>
        (t.name || '').toLowerCase().includes(kw) ||
        (t.description || '').toLowerCase().includes(kw) ||
        (t.category || '').toLowerCase().includes(kw)
      );
    }
    return list;
  }, [templates, selectedCategory, searchKeyword]);

  const uncategorizedCount = useMemo(() => templates.filter(t => !t.category).length, [templates]);

  // ── 左側 Tree Menu items ──────────────────────────────────────────
  const menuItems = useMemo(() => {
    const items = [
      {
        key: ALL_KEY,
        icon: <AppstoreOutlined />,
        label: <span>{t('reportTemplates.all')} <Tag style={{ marginLeft: 4 }}>{templates.length}</Tag></span>,
      },
    ];
    categories.forEach(cat => {
      const cnt = templates.filter(t => t.category === cat).length;
      items.push({
        key: cat,
        icon: <FolderOutlined />,
        label: <span>{cat} <Tag style={{ marginLeft: 4 }}>{cnt}</Tag></span>,
      });
    });
    if (uncategorizedCount > 0) {
      items.push({
        key: UNCATEGORIZED_KEY,
        icon: <FolderOutlined style={{ color: '#aaa' }} />,
        label: <span style={{ color: '#aaa' }}>{t('reportTemplates.uncategorized')} <Tag style={{ marginLeft: 4 }}>{uncategorizedCount}</Tag></span>,
      });
    }
    return items;
  }, [templates, categories, uncategorizedCount, t]);

  // ── 批量上傳邏輯 ──────────────────────────────────────────────────
  const openUploadDrawer = () => {
    setQueue([]);
    setBatchDone(false);
    setBatchCategory('');
    setUploadDrawerOpen(true);
  };

  const closeUploadDrawer = () => {
    setUploadDrawerOpen(false);
    if (batchDone) loadTemplates();
  };

  const handleFilesAdded = async (newFiles) => {
    const directItems = [];
    const zipPromises = [];

    for (const f of newFiles) {
      const ext = f.name.split('.').pop()?.toLowerCase();
      if (ext === 'zip') {
        zipPromises.push(
          JSZip.loadAsync(f).then((zip) => {
            const extracted = [];
            zip.forEach((relativePath, entry) => {
              if (entry.dir) return;
              const entryExt = relativePath.split('.').pop()?.toLowerCase();
              if (!VALID_TEMPLATE_EXTS.has(entryExt)) return;
              // 只取檔名，忽略 ZIP 內路徑
              const entryName = relativePath.split('/').pop();
              extracted.push(
                entry.async('blob').then((blob) => {
                  const file = new File([blob], entryName, { type: blob.type });
                  return {
                    uid: `${entryName}-${blob.size}-${Date.now()}-${Math.random()}`,
                    file,
                    name: fileBaseName(entryName),
                    status: 'pending',
                    result: null,
                    fromZip: f.name,
                  };
                })
              );
            });
            return Promise.all(extracted);
          }).catch(() => {
            message.warning(`ZIP 解壓失敗：${f.name}`);
            return [];
          })
        );
      } else if (VALID_TEMPLATE_EXTS.has(ext)) {
        directItems.push({
          uid: f.uid || `${f.name}-${f.size}-${Date.now()}`,
          file: f,
          name: fileBaseName(f.name),
          status: 'pending',
          result: null,
        });
      }
    }

    const zipResults = await Promise.all(zipPromises);
    const allNew = [...directItems, ...zipResults.flat()];

    setQueue((prev) => {
      const existing = new Set(prev.map((q) => `${q.file.name}|${q.file.size}`));
      return [...prev, ...allNew.filter((i) => !existing.has(`${i.file.name}|${i.file.size}`))];
    });
  };

  const updateQueueItem = (uid, patch) =>
    setQueue((prev) => prev.map((q) => q.uid === uid ? { ...q, ...patch } : q));

  const removeQueueItem = (uid) =>
    setQueue((prev) => prev.filter((q) => q.uid !== uid));

  const handleStartBatch = async () => {
    const pending = queue.filter((q) => q.status === 'pending');
    if (pending.length === 0) return;
    setBatchRunning(true);
    for (const item of pending) {
      updateQueueItem(item.uid, { status: 'uploading' });
      try {
        const result = await reportTemplatesApi.upload(item.file, item.name, '', batchCategory || undefined);
        updateQueueItem(item.uid, { status: 'done', result });
      } catch (e) {
        updateQueueItem(item.uid, { status: 'error', result: e?.message || String(e) });
      }
    }
    setBatchRunning(false);
    setBatchDone(true);
    loadTemplates();
  };

  const pendingCount = queue.filter((q) => q.status === 'pending').length;
  const doneCount = queue.filter((q) => q.status === 'done').length;
  const errorCount = queue.filter((q) => q.status === 'error').length;

  // ── 批量刪除 ─────────────────────────────────────────────────────
  const handleBatchDelete = () => {
    Modal.confirm({
      title: t('reportTemplates.confirmBatchDelete', { count: selectedRowKeys.length }),
      icon: <ExclamationCircleOutlined />,
      okText: t('reportTemplates.delete'),
      okType: 'danger',
      cancelText: t('reportTemplates.cancel'),
      onOk: async () => {
        setBatchDeleting(true);
        try {
          await reportTemplatesApi.batchDelete(selectedRowKeys);
          message.success(t('reportTemplates.batchDeleteSuccess', { count: selectedRowKeys.length }));
          setSelectedRowKeys([]);
          loadTemplates();
        } catch (e) {
          message.error(t('reportTemplates.batchDeleteFailed') + (e?.message || e));
        } finally {
          setBatchDeleting(false);
        }
      },
    });
  };

  // ── 下載 ─────────────────────────────────────────────────────────
  const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownload = async (record) => {
    try {
      const fallback = `${record.name}.${record.fileType || 'docx'}`;
      const { blob, filename } = await reportTemplatesApi.download(record.id, fallback);
      triggerBlobDownload(blob, filename);
    } catch (e) {
      message.error(t('reportTemplates.downloadFailed') + (e?.message || e));
    }
  };

  const handleBatchDownload = async () => {
    if (selectedRowKeys.length === 0) return;
    setBatchDownloading(true);
    try {
      const { blob, filename } = await reportTemplatesApi.batchDownload(selectedRowKeys);
      triggerBlobDownload(blob, filename);
      message.success(t('reportTemplates.batchDownloadSuccess', { done: selectedRowKeys.length }));
    } catch (e) {
      message.error(t('reportTemplates.batchDownloadFailed', { error: 1 }) + ' ' + (e?.message || e));
    } finally {
      setBatchDownloading(false);
    }
  };

  // ── 其他操作 ─────────────────────────────────────────────────────
  const handleOpenDetail = async (record) => {
    setDetailDrawerOpen(true);
    setDetailLoading(true);
    try {
      setCurrentTemplate(await reportTemplatesApi.get(record.id));
    } catch (e) {
      message.error(t('reportTemplates.loadDetailFailed') + (e?.message || e));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await reportTemplatesApi.delete(id);
      message.success(t('reportTemplates.deleteSuccess'));
      setSelectedRowKeys(k => k.filter(k => k !== id));
      loadTemplates();
    } catch (e) {
      message.error(t('reportTemplates.deleteFailed') + (e?.message || e));
    }
  };

  const handleEditMeta = (record) => {
    setEditTarget(record);
    editForm.setFieldsValue({ name: record.name, description: record.description, category: record.category || '' });
    setEditModalOpen(true);
  };

  const handleSaveMeta = async (values) => {
    try {
      await reportTemplatesApi.updateMeta(editTarget.id, values.name, values.description, values.category || null);
      message.success(t('reportTemplates.updateSuccess'));
      setEditModalOpen(false);
      loadTemplates();
    } catch (e) {
      message.error(t('reportTemplates.updateFailed') + (e?.message || e));
    }
  };

  // ── Table columns ─────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      title: t('reportTemplates.colName'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          {FILE_TYPE_ICON[record.fileType] || <FileUnknownOutlined />}
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: t('reportTemplates.colFormat'),
      dataIndex: 'fileType',
      key: 'fileType',
      width: 80,
      render: (ft) => (
        <Tag color={FILE_TYPE_COLOR[ft] || 'default'}>
          {FILE_TYPE_LABEL[ft] || ft?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: t('reportTemplates.colCategory'),
      dataIndex: 'category',
      key: 'category',
      width: 110,
      render: (cat) => cat
        ? <Tag icon={<TagsOutlined />} color="geekblue">{cat}</Tag>
        : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
    },
    {
      title: t('reportTemplates.colPlaceholder'),
      key: 'placeholders',
      width: 160,
      render: (_, record) => {
        const s = record.placeholderSummary || {};
        return (
          <Space size={4}>
            {s.textCount > 0 && <Tag color="blue">{t('reportTemplates.textCount', { count: s.textCount })}</Tag>}
            {s.photoCount > 0 && <Tag color="orange">{t('reportTemplates.photoCount', { count: s.photoCount })}</Tag>}
            {!s.textCount && !s.photoCount && <Text type="secondary">—</Text>}
          </Space>
        );
      },
    },
    {
      title: t('reportTemplates.colUploadDate'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('reportTemplates.colActions'),
      key: 'actions',
      width: 145,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title={t('reportTemplates.viewPlaceholder')}>
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleOpenDetail(record)} />
          </Tooltip>
          <Tooltip title={t('reportTemplates.download')}>
            <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(record)} />
          </Tooltip>
          <Tooltip title={t('reportTemplates.edit')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditMeta(record)} />
          </Tooltip>
          <Popconfirm
            title={t('reportTemplates.confirmDeleteOne')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('reportTemplates.delete')} cancelText={t('reportTemplates.cancel')}
          >
            <Tooltip title={t('reportTemplates.delete')}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ], [t, handleOpenDetail, handleDownload, handleEditMeta, handleDelete]);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>{t('reportTemplates.title')}</Title>
          <Text type="secondary">{t('reportTemplates.subtitle')}</Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadTemplates}>{t('reportTemplates.refresh')}</Button>
            <Button type="primary" icon={<CloudUploadOutlined />} onClick={openUploadDrawer}>
              {t('reportTemplates.uploadTemplate')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Body: left tree + right table */}
      <Row gutter={12} align="top">
        {/* Left category menu */}
        <Col flex="200px">
          <Card
            size="small"
            bodyStyle={{ padding: 0 }}
            style={{ position: 'sticky', top: 16 }}
          >
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
              <Text strong style={{ fontSize: 12, color: '#666' }}>{t('reportTemplates.categories')}</Text>
            </div>
            <Menu
              mode="inline"
              selectedKeys={[selectedCategory]}
              onSelect={({ key }) => {
                setSelectedCategory(key);
                setSelectedRowKeys([]);
              }}
              items={menuItems}
              style={{ border: 'none', fontSize: 13 }}
              inlineIndent={12}
            />
          </Card>
        </Col>

        {/* Right table */}
        <Col flex="1">
          <Card size="small">
            {/* 搜尋欄 */}
            <div style={{ marginBottom: 10 }}>
              <Input
                allowClear
                prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                suffix={searchKeyword
                  ? <CloseOutlined style={{ color: '#bbb', cursor: 'pointer' }} onClick={() => setSearchKeyword('')} />
                  : null}
                placeholder={t('reportTemplates.searchPlaceholder')}
                value={searchKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                style={{ maxWidth: 320 }}
              />
              {searchKeyword && (
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  {t('reportTemplates.searchResultCount', { count: filteredTemplates.length })}
                </Text>
              )}
            </div>

            {/* Batch action bar */}
            {selectedRowKeys.length > 0 && (
              <div style={{
                background: '#e6f4ff',
                border: '1px solid #91caff',
                borderRadius: 6,
                padding: '8px 12px',
                marginBottom: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}>
                <Text>{t('reportTemplates.selectedCount', { count: selectedRowKeys.length })}</Text>
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  loading={batchDownloading}
                  onClick={handleBatchDownload}
                >
                  {t('reportTemplates.batchDownload')}
                </Button>
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={batchDeleting}
                  onClick={handleBatchDelete}
                >
                  {t('reportTemplates.batchDelete')}
                </Button>
                <Button
                  size="small"
                  onClick={() => setSelectedRowKeys([])}
                >
                  {t('reportTemplates.clearSelection')}
                </Button>
              </div>
            )}

            <Table
              rowKey="id"
              dataSource={filteredTemplates}
              columns={columns}
              loading={loading}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                preserveSelectedRowKeys: true,
              }}
              pagination={{
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '30', '50', '100'],
                showTotal: (total, range) => `${range[0]}–${range[1]} / ${total} 筆`,
                onShowSizeChange: (_, size) => setPageSize(size),
              }}
              locale={{ emptyText: t('reportTemplates.emptyInCategory') }}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {/* ── 批量上傳 Drawer ── */}
      <Drawer
        title={t('reportTemplates.uploadTemplate')}
        open={uploadDrawerOpen}
        onClose={closeUploadDrawer}
        width={580}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={closeUploadDrawer}>
                {batchDone ? t('reportTemplates.close') : t('reportTemplates.cancel')}
              </Button>
              {!batchDone && (
                <Button
                  type="primary"
                  icon={batchRunning ? <LoadingOutlined /> : <CloudUploadOutlined />}
                  loading={batchRunning}
                  disabled={pendingCount === 0}
                  onClick={handleStartBatch}
                >
                  {batchRunning
                    ? t('reportTemplates.uploadingProgress', {
                      done: queue.filter(q => q.status === 'done' || q.status === 'error').length,
                      total: queue.length,
                    })
                    : t('reportTemplates.uploadAll', { count: pendingCount })}
                </Button>
              )}
            </Space>
          </Row>
        }
      >
        {/* 共用分類輸入 */}
        {!batchDone && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('reportTemplates.batchCategoryLabel')}</Text>
            <AutoComplete
              value={batchCategory}
              onChange={setBatchCategory}
              options={categories.map(c => ({ value: c }))}
              filterOption={(input, opt) => opt.value.toLowerCase().includes(input.toLowerCase())}
              placeholder={t('reportTemplates.categoryPlaceholder')}
              style={{ width: '100%', marginTop: 4 }}
              disabled={batchRunning}
            />
          </div>
        )}

        {/* 拖曳選檔區 */}
        {!batchDone && (
          <Upload.Dragger
            accept={ACCEPTED_FORMATS}
            multiple
            showUploadList={false}
            beforeUpload={(file, fileList) => {
              if (file === fileList[0]) handleFilesAdded(Array.from(fileList));
              return false;
            }}
            style={{ marginBottom: 16 }}
            disabled={batchRunning}
          >
            <p className="ant-upload-drag-icon">
              <CloudUploadOutlined style={{ fontSize: 36, color: '#1677ff' }} />
            </p>
            <p className="ant-upload-text">{t('reportTemplates.dragHint')}</p>
            <p className="ant-upload-hint">{t('reportTemplates.dragSubHint')}</p>
          </Upload.Dragger>
        )}

        {batchDone && (
          <Result
            status={errorCount === 0 ? 'success' : 'warning'}
            title={t('reportTemplates.uploadComplete', { done: doneCount, error: errorCount })}
            style={{ paddingTop: 8, paddingBottom: 8 }}
          />
        )}

        {queue.length > 0 && (
          <>
            <Divider style={{ margin: '12px 0' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('reportTemplates.fileCount', { count: queue.length })}
                {pendingCount > 0 && !batchRunning && `　${t('reportTemplates.pendingUpload', { count: pendingCount })}`}
                {batchRunning && `　${t('reportTemplates.uploading')}`}
                {batchDone && `　${t('reportTemplates.completed')}`}
              </Text>
            </Divider>
            <List
              dataSource={queue}
              style={{ maxHeight: 400, overflowY: 'auto' }}
              renderItem={(item) => {
                const ext = item.file.name.split('.').pop()?.toLowerCase();
                const icon = FILE_TYPE_ICON[ext] || <FileUnknownOutlined />;
                return (
                  <List.Item
                    style={{ padding: '6px 4px' }}
                    actions={
                      item.status === 'pending' && !batchRunning
                        ? [<Button key="del" type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removeQueueItem(item.uid)} />]
                        : undefined
                    }
                  >
                    <List.Item.Meta
                      avatar={
                        <span style={{ fontSize: 20 }}>
                          {item.status === 'uploading' && <LoadingOutlined style={{ color: '#1677ff' }} />}
                          {item.status === 'done' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                          {item.status === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                          {item.status === 'pending' && icon}
                        </span>
                      }
                      title={
                        item.status === 'pending' && !batchRunning ? (
                          <Input
                            size="small"
                            value={item.name}
                            onChange={(e) => updateQueueItem(item.uid, { name: e.target.value })}
                            style={{ fontWeight: 500 }}
                          />
                        ) : (
                          <Text style={{ fontWeight: 500 }} type={item.status === 'error' ? 'danger' : undefined}>
                            {item.name}
                          </Text>
                        )
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {item.file.name}
                          {item.status === 'done' && item.result && (
                            <> <Tag color="orange" style={{ fontSize: 10 }}>{t('reportTemplates.photoSlotCount', { count: item.result.placeholderSummary?.photoCount ?? 0 })}</Tag></>
                          )}
                          {item.status === 'error' && (
                            <Text type="danger" style={{ fontSize: 11 }}> {item.result}</Text>
                          )}
                        </Text>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </>
        )}

        <Divider />
        <Text type="secondary" style={{ fontSize: 12 }}>
          <Text strong>{t('reportTemplates.placeholderFormatHint')}</Text>
          {' '}{t('reportTemplates.placeholderFormatExamples')}
        </Text>
      </Drawer>

      {/* ── 詳情 Drawer ── */}
      <Drawer
        title={currentTemplate
          ? t('reportTemplates.detailTitleWithName', { name: currentTemplate.name })
          : t('reportTemplates.detailTitle')}
        open={detailDrawerOpen}
        onClose={() => { setDetailDrawerOpen(false); setCurrentTemplate(null); }}
        width={560}
      >
        {detailLoading
          ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          : currentTemplate
            ? <PlaceholderDetailView template={currentTemplate} onBindingsSaved={loadTemplates} />
            : null}
      </Drawer>

      {/* ── 編輯 Modal ── */}
      <Modal
        title={t('reportTemplates.editTitle')}
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={() => editForm.submit()}
        okText={t('reportTemplates.save')} cancelText={t('reportTemplates.cancel')}
      >
        <Form form={editForm} layout="vertical" onFinish={handleSaveMeta}>
          <Form.Item label={t('reportTemplates.colName')} name="name" rules={[{ required: true, message: t('reportTemplates.nameRequired') }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item label={t('reportTemplates.colCategory')} name="category">
            <AutoComplete
              options={categories.map(c => ({ value: c }))}
              filterOption={(input, opt) => opt.value.toLowerCase().includes(input.toLowerCase())}
              placeholder={t('reportTemplates.categoryPlaceholder')}
            />
          </Form.Item>
          <Form.Item label={t('reportTemplates.description')} name="description">
            <TextArea rows={3} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ── Placeholder 詳情與綁定 ────────────────────────────────────────────────────

function PlaceholderDetailView({ template, onBindingsSaved }) {
  const { t } = useLanguage();
  const [bindings, setBindings] = useState(template.variableBindings || {});
  const [saving, setSaving] = useState(false);

  const placeholders = template.placeholders || [];
  const textPhs = placeholders.filter(p => p.type !== 'PHOTO');
  const photoPhs = placeholders.filter(p => p.type === 'PHOTO');

  const handleSaveBindings = async () => {
    setSaving(true);
    try {
      await reportTemplatesApi.updateBindings(template.id, bindings);
      message.success(t('reportTemplates.bindingsSaved'));
      if (onBindingsSaved) onBindingsSaved();
    } catch (e) {
      message.error(t('reportTemplates.saveFailed') + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const updateBinding = (raw, field, value) =>
    setBindings(prev => ({ ...prev, [raw]: { ...(prev[raw] || {}), [field]: value } }));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      <div>
        <Tag color={FILE_TYPE_COLOR[template.fileType] || 'default'}>
          {FILE_TYPE_LABEL[template.fileType] || template.fileType}
        </Tag>
        {template.category && <Tag color="geekblue" icon={<TagsOutlined />}>{template.category}</Tag>}
        <Text type="secondary" style={{ fontSize: 12 }}>{template.originalFileName}</Text>
      </div>

      {textPhs.length > 0 && (
        <Card
          size="small"
          title={<Badge count={textPhs.length} style={{ backgroundColor: '#1677ff' }} offset={[8, 0]}>{t('reportTemplates.textPlaceholders')}</Badge>}
          extra={<Button size="small" type="primary" loading={saving} onClick={handleSaveBindings}>{t('reportTemplates.saveBindings')}</Button>}
        >
          {textPhs.map((ph, idx) => (
            <div key={idx} style={{ marginBottom: 10 }}>
              <Space align="start" style={{ width: '100%' }}>
                <Tag color={PLACEHOLDER_TYPE_COLOR[ph.type] || 'default'}>{ph.type}</Tag>
                <div style={{ flex: 1 }}>
                  <Text code style={{ fontSize: 12 }}>{ph.raw}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>{t('reportTemplates.location', { location: ph.location })}</Text>
                  <div style={{ marginTop: 4 }}>
                    <Input
                      size="small"
                      placeholder={t('reportTemplates.displayLabelPlaceholder')}
                      value={bindings[ph.raw]?.displayLabel || ''}
                      onChange={e => updateBinding(ph.raw, 'displayLabel', e.target.value)}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </Space>
            </div>
          ))}
        </Card>
      )}

      {photoPhs.length > 0 && (
        <Card
          size="small"
          title={<Badge count={photoPhs.length} style={{ backgroundColor: '#fa8c16' }} offset={[8, 0]}>{t('reportTemplates.photoSlots')}</Badge>}
        >
          {photoPhs.map((ph, idx) => (
            <div key={idx} style={{ marginBottom: 8 }}>
              <Space>
                <Tag color="orange">PHOTO</Tag>
                <Text strong>{ph.slotLabel || ph.key}</Text>
                {ph.options && Object.entries(ph.options).map(([k, v]) => (
                  <Tag key={k}>{k}={v}</Tag>
                ))}
              </Space>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>{t('reportTemplates.location', { location: ph.location })}</Text>
            </div>
          ))}
        </Card>
      )}

      {placeholders.length === 0 && (
        <Text type="secondary">
          {t('reportTemplates.noPlaceholdersDetected')}
        </Text>
      )}
    </Space>
  );
}
